import DEFAULTS from 'core/data-model/constants/defaults'
import MESSAGES from 'core/data-model/constants/messages'
import { calculateCoverageDeathBenefit } from 'core/service/investment'
import { formatNumber } from 'core/service/lib/number-format'

import _ from 'lodash'
import {
  getModelFactors,
  getModelFactorsForUser,
  getTotalRiderPremium,
  getSelectedRiders,
} from 'quick-quote/product-common/coverage-plan/selectors'
import {
  getRppFundAllocations,
  getRppTotalAllocations,
} from 'quick-quote/product-investment/fund-allocation/rpp/selectors'
import {
  getDisplayProducts,
  getSelectedDisplayProduct,
  getSelectedDisplayProductCode,
} from 'quick-quote/product-selection/selectors'
import { createSelector } from 'reselect'
import { hasRiderErrors } from 'quick-quote/product-investment/coverage-plan/sagas'

export const getRegularPremium = (state) => state.investment.coveragePlan.selectedBasicPlan.regularPremium

export const getRegularTopUp = (state) => state.investment.coveragePlan.selectedBasicPlan.regularTopUp

export const getRegularPremiumRatio = (state) => state.investment.coveragePlan.selectedBasicPlan.regularPremiumRatio

export const getSelectedModelFactorID = (state) => state.investment.coveragePlan.selectedModelFactorID

export const getPreviousModelFactorID = (state) => state.investment.coveragePlan.previousModelFactorID

export const getSumAssured = (state) => state.investment.coveragePlan.selectedBasicPlan.sumAssured

export const getExpectedReturnComparisons = (state) => state.investment.coveragePlan.expectedReturnComparisons.value

export const getLumpSumTopUp = (state) => _.get(_.first(getExpectedReturnComparisons(state)), 'lumpSum.value', 0)

export const getLumpSumErrors = (state) => state.investment.coveragePlan.expectedReturnComparisons.errors

export const hasFirstYearLstu = createSelector(
  getLumpSumTopUp,
  getLumpSumErrors,
  (lstu, errors) => lstu > 0 && errors.length === 0
)

export const getPayFirstMonthMessage = (state) => {
  const modelFactorID = getSelectedModelFactorID(state)
  const regularPremium = getRegularPremium(state)
  const regularTopUp = getRegularTopUp(state)
  const totalPremium = regularPremium.value + regularTopUp.value
  return modelFactorID !== DEFAULTS.SELECTED_MODEL_FACTOR_MONTHLY
    ? ''
    : `(${MESSAGES.PAY_FIRST_MONTH} ${formatNumber(totalPremium * 2)} ${MESSAGES.BAHT})`
}

export const getPayFirstMonthAndTotalRiderMessage = (state) => {
  const modelFactorID = getSelectedModelFactorID(state)
  const regularPremium = getRegularPremium(state)
  const totalRiderPremium = getTotalRiderPremium(state)
  const totalPremium = regularPremium.value + totalRiderPremium
  return modelFactorID !== DEFAULTS.SELECTED_MODEL_FACTOR_MONTHLY
    ? ''
    : `(${MESSAGES.PAY_FIRST_MONTH} ${formatNumber(totalPremium * 2, 2)} ${MESSAGES.BAHT})`
}

export const getLumpSum = createSelector(getExpectedReturnComparisons, (expectedReturnComparisons) =>
  expectedReturnComparisons.reduce((acc, { year, lumpSum }) => {
    if (lumpSum === 'DISALLOWED') {
      return acc
    }
    return acc.concat([{ year, value: lumpSum.value }])
  }, [])
)

export const getCoverageDeathBenefit = (state) => {
  const displayProduct = getSelectedDisplayProduct(state)
  return calculateCoverageDeathBenefit(displayProduct.code, getSumAssured(state).value)
}

export const getCoveragePeriod = (state) => {
  let displayProduct = getDisplayProducts(state)
  let selectedProduct = getSelectedDisplayProductCode(state)
  let period = {
    type: 'limited_by_duration',
    value: 0,
  }

  displayProduct.forEach((product, index) => {
    if (product.code === selectedProduct) {
      period = product.coveragePeriod
    }
  })
  return period.value
}

export const getAvailableModelFactors = createSelector(
  getSelectedDisplayProduct,
  getModelFactorsForUser,
  (selectedBasicPlan, modelFactors) =>
    modelFactors.filter((modelFactor) =>
      selectedBasicPlan && selectedBasicPlan ? selectedBasicPlan.modelFactors.indexOf(modelFactor.id) > -1 : false
    )
)

export const getSelectedModelFactor = createSelector(
  getModelFactors,
  getSelectedModelFactorID,
  (modelFactors, selectedModelFactorID) => _.find(modelFactors, { id: selectedModelFactorID })
)

export const getSelectedPaymentFrequency = createSelector(getSelectedModelFactor, (selectedModelFactor) =>
  _.get(selectedModelFactor, 'paymentFrequency', '-')
)
export const getSelectedModelFactorLabel = createSelector(getSelectedModelFactor, (selectedModelFactor) =>
  _.get(selectedModelFactor, 'displayLabel', '-')
)

export const getSelectedModelFactorValue = createSelector(getSelectedModelFactor, (modelFactor) =>
  modelFactor ? modelFactor.factor : 1
)

export const getSelectedModelFactorILPValue = createSelector(getSelectedModelFactor, (selectedModelFactor) =>
  selectedModelFactor ? selectedModelFactor.factorILP : 0
)

export const getSelectedModelFactorPeriod = createSelector(getSelectedModelFactor, (selectedModelFactor) =>
  selectedModelFactor ? selectedModelFactor.periods : 0
)

export const _isValidSumAssured = (sumAssured) => {
  return sumAssured.value !== 0 && !sumAssured.errors.some((e) => e.type === 'VALIDATION')
}
export const _isValidTopUp = (regularTopUp) => {
  return regularTopUp.errors.length === 0
}
export const _isValidPremium = (regularPremium) => {
  return regularPremium.value !== 0 && regularPremium.errors.length === 0
}

export const isLumpSumValid = createSelector(getLumpSumErrors, (lumpSumErrors) => _.isEmpty(lumpSumErrors))

export const isPremiumPageValid = createSelector(
  getSumAssured,
  getRegularTopUp,
  getRegularPremium,
  getSelectedRiders,
  (sumAssured, regularTopUp, regularPremium, riders) => {
    return (
      _isValidSumAssured(sumAssured) &&
      _isValidTopUp(regularTopUp) &&
      _isValidPremium(regularPremium) &&
      !hasRiderErrors(riders)
    )
  }
)

export const _isFundPageValid = (totalAllocation, fundAllocation) => {
  const fullAllocation = 100
  return totalAllocation === fullAllocation && !fundAllocation.some(({ errors }) => !_.isEmpty(errors))
}

export const isFundPageValid = createSelector(
  getRppFundAllocations,
  getRppTotalAllocations,
  (fundAllocation, totalAllocation) => _isFundPageValid(totalAllocation.value, fundAllocation)
)
